import { Code2, PenTool, Compass, Layers, ArrowUpRight, PlayCircle, Check, X, Sparkles } from "lucide-react";

const pillars = [
  {
    id: "engineering",
    label: "Engineering & Code",
    icon: Code2,
    title: "Read the code. Follow the workflow.",
    body:
      "High-quality streaming optimized for technical content keeps code, terminals, IDEs, diagrams and interfaces readable across devices and network conditions.",
    body2:
      "Vibecast is designed around the reality that professional learning often happens inside code editors, terminals, architecture diagrams and complex interfaces.",
  },
  {
    id: "design",
    label: "Design & Product",
    icon: PenTool,
    title: "Learn by seeing the work.",
    body:
      "Technical knowledge is often visual. A design system, Figma workflow, product prototype, architecture diagram or debugging session cannot always be understood through slides and talking heads.",
    body2:
      "Vibecast is designed for screen-led learning, making the actual workflow the centre of the experience.",
    accent: true,
  },
  {
    id: "discovery",
    label: "Focused Discovery",
    icon: Compass,
    title: "Discover with intent.",
    body:
      "Vibecast is designed around what you are trying to learn, not everything you might watch next.",
    body2:
      "Professional content is organized around skills, disciplines and learning intent, creating a discovery experience designed to keep users inside their professional context.",
  },
  {
    id: "context",
    label: "Professional Context",
    icon: Layers,
    title: "A platform built around professional knowledge.",
    body:
      "Engineering, design, product, data, cloud, cybersecurity and emerging technologies have different content structures, audiences and learning patterns.",
    body2:
      "Vibecast is designed to understand those professional contexts rather than treating every video as interchangeable content.",
  },
];

const compareRows = [
  ["Optimized for engagement", "Optimized for learning intent"],
  ["Broad content universe", "Professional knowledge ecosystem"],
  ["Entertainment-driven discovery", "Context-driven discovery"],
  ["Mixed-interest recommendations", "Professional-context recommendations"],
  ["General-purpose interface", "Designed for technical content"],
];

const creatorPersonas = [
  "Engineers",
  "Designers",
  "Architects",
  "Product leaders",
  "Technology educators",
];

export const WhatWeBuild = () => {
  return (
    <section
      id="vibecast"
      data-testid="vibecast-section"
      className="relative py-12 sm:py-16 border-t border-black/5 bg-zinc-50/40"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        {/* Section header + thesis — balanced 2-column */}
        <div className="grid grid-cols-12 gap-8 lg:gap-12 mb-14 items-end">
          <div className="col-span-12 lg:col-span-6">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-[#0066FF]">
              Flagship Venture
            </p>
            <h2 className="font-display mt-4 text-5xl sm:text-6xl lg:text-[5.5rem] font-bold tracking-[-0.03em] text-zinc-950 leading-[0.98]">
              Vibecast<span className="text-[#0066FF]">.</span>
            </h2>
            <div className="mt-5 inline-flex items-center gap-2 px-3 h-8 rounded-full border border-black/10 bg-white text-[11px] font-semibold uppercase tracking-[0.22em] text-zinc-700">
              <PlayCircle className="w-3.5 h-3.5 text-[#0066FF]" />
              Streaming application for tech professionals
            </div>
          </div>
          <div className="col-span-12 lg:col-span-6">
            <div
              data-testid="vibecast-thesis"
              className="text-lg sm:text-xl text-zinc-700 font-light leading-relaxed space-y-5"
            >
              <p>
                Mainstream video platforms are built to maximize engagement
                across everything a viewer might watch. That works
                exceptionally well for entertainment, but it creates a poor
                environment for focused professional learning.
              </p>
              <p>
                <span className="font-medium text-zinc-950 bg-[#0066FF]/10 px-1.5 py-0.5 rounded">
                  Vibecast takes a different approach.
                </span>{" "}
                It is a streaming environment designed around professional
                intent, where the platform, interface, content and discovery
                experience are built to help people stay focused on what they
                came to learn.
              </p>
            </div>
          </div>
        </div>

        {/* Pillars — 4 across on large */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {pillars.map((p, i) => (
            <Pillar key={p.id} index={i} {...p} />
          ))}
        </div>

        {/* Why Vibecast — 12-col mosaic */}
        <div
          id="vibecast-studio"
          data-testid="vibecast-why-card"
          className="mt-10 rounded-3xl overflow-hidden border border-black/5 bg-zinc-950 text-white relative"
        >
          <div className="absolute inset-0 grid-bg-dark opacity-60" aria-hidden />
          <div
            aria-hidden
            className="absolute -right-32 -top-32 w-[500px] h-[500px] rounded-full blur-3xl"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0,102,255,0.32), transparent 70%)",
            }}
          />

          <div className="relative grid grid-cols-12 gap-8 lg:gap-14 p-8 sm:p-12">
            {/* Left column — 7 cols */}
            <div className="col-span-12 lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full border border-white/15 bg-white/5 text-[10px] font-semibold uppercase tracking-[0.22em] text-white/85">
                <Sparkles className="w-3 h-3 text-[#6DA6FF]" />
                Why Vibecast?
              </div>

              <h3 className="font-display mt-6 text-4xl sm:text-5xl lg:text-[3.75rem] font-bold tracking-[-0.025em] leading-[1.02] text-white">
                The internet optimized video for{" "}
                <span className="italic font-medium text-white/60">attention</span>.
                We want to optimize it for{" "}
                <span className="text-[#6DA6FF]">progress</span>.
              </h3>

              <p className="mt-6 text-white/70 font-light leading-relaxed max-w-2xl text-[15px] sm:text-base">
                Video transformed how the world learns. But the dominant
                platforms were designed around a different objective: maximize
                engagement.
                <br />
                Professional learning has a different requirement.
              </p>
            </div>

            {/* Right column — 5 cols, quote-style stack */}
            <div className="col-span-12 lg:col-span-5 flex flex-col gap-5">
              <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur p-6 sm:p-7">
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6DA6FF]">
                  The distinction
                </span>
                <h4 className="font-display mt-3 text-2xl sm:text-[26px] font-semibold tracking-[-0.01em] text-white leading-snug">
                  You don&apos;t always want to watch more.
                  <br />
                  <span className="text-[#6DA6FF]">You want to understand more.</span>
                </h4>
                <p className="mt-4 text-white/65 font-light leading-relaxed text-[14.5px]">
                  Vibecast is being built for that distinction. It creates a
                  focused environment where technology professionals can
                  discover, watch and learn without being constantly pulled
                  into unrelated entertainment.
                </p>
              </div>

              <div className="rounded-2xl border border-[#0066FF]/30 bg-[#0066FF]/10 p-6 sm:p-7">
                <span className="text-[10px] uppercase tracking-[0.22em] font-semibold text-[#6DA6FF]">
                  The principle
                </span>
                <h4 className="font-display mt-3 text-2xl sm:text-[26px] font-semibold tracking-[-0.01em] text-white leading-snug">
                  Less distraction.{" "}
                  <span className="text-[#6DA6FF]">More deliberate learning.</span>
                </h4>
              </div>
            </div>
          </div>
        </div>

        {/* Built Differently — full-width comparison */}
        <div
          data-testid="vibecast-built-differently"
          className="mt-10 rounded-3xl border border-black/5 bg-white overflow-hidden"
        >
          <div className="grid grid-cols-12 gap-6 p-8 sm:p-10 border-b border-black/5 items-end">
            <div className="col-span-12 lg:col-span-7">
              <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full border border-black/10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-700">
                <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" />
                Built Differently
              </div>
              <h3 className="font-display mt-5 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-[-0.02em] text-zinc-950 leading-[1.05]">
                A different objective changes every part of the product.
              </h3>
            </div>
            <div className="col-span-12 lg:col-span-5">
              <p className="text-zinc-600 font-light leading-relaxed text-[15px]">
                Same medium. Opposite intent. Every mechanic below is designed
                against the mainstream default and towards professional
                learning outcomes.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[560px]" data-testid="vibecast-compare-table">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-[0.22em] font-semibold text-zinc-500 bg-zinc-50/70">
                  <th className="px-6 sm:px-10 py-4 border-b border-black/5 w-1/2">
                    Mainstream video
                  </th>
                  <th className="px-6 sm:px-10 py-4 border-b border-black/5 w-1/2 text-[#0066FF]">
                    Vibecast
                  </th>
                </tr>
              </thead>
              <tbody>
                {compareRows.map(([left, right], i) => (
                  <tr
                    key={i}
                    data-testid={`vibecast-compare-row-${i}`}
                    className={`text-[14.5px] ${
                      i % 2 === 0 ? "bg-white" : "bg-zinc-50/40"
                    }`}
                  >
                    <td className="px-6 sm:px-10 py-4 border-b border-black/5 text-zinc-500 align-top">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-1 w-4 h-4 grid place-items-center rounded-full bg-zinc-200 text-zinc-500 shrink-0">
                          <X className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                        <span>{left}</span>
                      </div>
                    </td>
                    <td className="px-6 sm:px-10 py-4 border-b border-black/5 text-zinc-950 align-top font-medium">
                      <div className="flex items-start gap-2.5">
                        <span className="mt-1 w-4 h-4 grid place-items-center rounded-full bg-[#0066FF]/10 text-[#0066FF] shrink-0">
                          <Check className="w-2.5 h-2.5" strokeWidth={3} />
                        </span>
                        <span>{right}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* A Better Home for Technical Creators — full-width mosaic */}
        <div
          data-testid="vibecast-creators-home"
          className="mt-10 rounded-3xl border border-black/5 bg-white p-8 sm:p-12 relative overflow-hidden"
        >
          <div
            aria-hidden
            className="absolute -left-32 -bottom-32 w-[500px] h-[500px] rounded-full blur-3xl opacity-70"
            style={{
              background:
                "radial-gradient(circle at center, rgba(0,102,255,0.15), transparent 70%)",
            }}
          />

          <div className="relative grid grid-cols-12 gap-8 lg:gap-12 items-start">
            <div className="col-span-12 lg:col-span-5">
              <div className="inline-flex items-center gap-2 px-3 h-7 rounded-full border border-black/10 bg-zinc-50 text-[10px] font-semibold uppercase tracking-[0.22em] text-zinc-700">
                <ArrowUpRight className="w-3 h-3 text-[#0066FF]" />
                For Creators
              </div>
              <h3 className="font-display mt-5 text-3xl sm:text-4xl lg:text-[2.75rem] font-bold tracking-[-0.02em] text-zinc-950 leading-[1.02]">
                A better home for technical creators.
              </h3>

              {/* Personas chips */}
              <div className="mt-8 flex flex-wrap gap-2">
                {creatorPersonas.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center gap-1.5 px-3 h-8 rounded-full border border-black/10 text-[12px] text-zinc-700 bg-white hover:border-zinc-950 hover:text-zinc-950 transition-colors"
                    data-testid={`creator-persona-${p.toLowerCase().replace(/\s/g, "-")}`}
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-[#0066FF]" />
                    {p}
                  </span>
                ))}
              </div>
            </div>

            <div className="col-span-12 lg:col-span-7 lg:pl-6 lg:border-l lg:border-black/5">
              <p className="text-[15.5px] sm:text-base text-zinc-700 font-light leading-relaxed">
                Vibecast gives engineers, designers, architects, product
                leaders, and technology educators a focused environment to
                share what they know with people who actively want to learn.
              </p>
              <p className="mt-4 text-[15.5px] sm:text-base text-zinc-700 font-light leading-relaxed">
                Instead of competing with entertainment for attention,
                creators can build an audience around expertise, professional
                experience, and real-world knowledge.
              </p>

              <div className="mt-8 rounded-2xl bg-zinc-950 text-white p-6 sm:p-7 relative overflow-hidden">
                <div
                  aria-hidden
                  className="absolute inset-0 grid-bg-dark opacity-40"
                />
                <p className="relative font-display text-xl sm:text-2xl lg:text-[26px] font-semibold tracking-[-0.01em] leading-snug">
                  Publish. Build your audience.{" "}
                  <span className="text-[#6DA6FF]">Share what you know.</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const Pillar = ({ icon: Icon, label, title, body, body2, accent, id, index }) => (
  <div
    data-testid={`vibecast-pillar-${id}`}
    className={`group relative rounded-2xl p-6 border transition-all duration-500 ease-out hover:-translate-y-1 flex flex-col ${
      accent
        ? "bg-zinc-950 border-zinc-900 text-white hover:border-[#0066FF]/50"
        : "bg-white border-black/5 hover:border-black/15 hover:shadow-[0_16px_48px_-20px_rgba(10,10,10,0.2)]"
    }`}
  >
    <div className="flex items-center justify-between">
      <div
        className={`w-10 h-10 grid place-items-center rounded-full border ${
          accent
            ? "border-white/15 bg-white/5 text-[#6DA6FF]"
            : "border-black/10 bg-zinc-50 text-[#0066FF]"
        }`}
      >
        <Icon className="w-4 h-4" />
      </div>
      <span
        className={`text-[10px] uppercase tracking-[0.22em] font-semibold ${
          accent ? "text-white/50" : "text-zinc-400"
        }`}
      >
        {String(index + 1).padStart(2, "0")}
      </span>
    </div>

    <div
      className={`mt-8 text-[10px] uppercase tracking-[0.22em] font-semibold ${
        accent ? "text-[#6DA6FF]" : "text-[#0066FF]"
      }`}
    >
      {label}
    </div>
    <h3
      className={`font-display mt-2 text-[20px] font-semibold tracking-tight leading-tight ${
        accent ? "text-white" : "text-zinc-950"
      }`}
    >
      {title}
    </h3>
    <p
      className={`mt-3 text-[13.5px] font-light leading-relaxed ${
        accent ? "text-white/70" : "text-zinc-600"
      }`}
    >
      {body}
    </p>
    {body2 && (
      <p
        className={`mt-3 text-[13.5px] font-light leading-relaxed ${
          accent ? "text-white/65" : "text-zinc-600"
        }`}
      >
        {body2}
      </p>
    )}

    {accent && (
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"
        style={{
          background:
            "radial-gradient(circle at 80% 20%, rgba(0,102,255,0.15), transparent 60%)",
        }}
      />
    )}
  </div>
);

export default WhatWeBuild;
